// GAVION training (/formation), English twin of training.fr.ts. Same keys, same %{arguments}.
import { productMissionsEn, trainingProductEn } from "./trainingProduct.en";
export const trainingEn = {
  title: "Training",
  subtitle:
    "Become a Dialer pro: mini-games on the dialer rules, and missions that complete themselves as you call.",
  load_error: "Could not load your progress.",
  retry: "Retry",
  // GAVION gavion-context: /formation/gavion « All about Gavion » (every signed-in user, read only)
  gavion_page: {
    link: "All about Gavion",
    title: "All about Gavion",
    subtitle:
      "Gavion, Gala, the plans, the terms and the answers to objections: what the AI also knows before it writes.",
    back: "Back to training",
    load_error: "Could not load the context.",
  },
  tabs: {
    label: "Training view",
    me: "My progress",
    team: "Team",
  },
  header: {
    level: "Level %{level}",
    xp_total: "%{xp} XP",
    xp_bar: "Progress to level %{level}",
    xp_next: "%{remaining} XP to level %{level}",
    missions_done: "%{done}/%{total} missions",
    badges: "Badges",
    level_short: "Level",
    badge_earned: "earned",
    badge_locked: "to earn",
  },
  ranks: {
    recruit: "Recruit",
    prospector: "Prospector",
    closer: "Closer",
    pro: "Dialer pro",
  },
  badges: {
    first_step: { name: "First step", desc: "A first mission completed." },
    flawless: { name: "Flawless", desc: "A quiz completed at 100%." },
    queue_master: {
      name: "Queue master",
      desc: "“Read the queue” without a mistake.",
    },
    fire_fingers: {
      name: "Fire fingers",
      desc: "90 points or more on the shortcuts.",
    },
    cool_head: {
      name: "Cool head",
      desc: "“Crisis situations” without a mistake.",
    },
    field_ready: {
      name: "In the field",
      desc: "Every field mission completed.",
    },
    graduate: {
      name: "Graduate",
      desc: 'The whole "Phone selling" course completed.',
    },
    gavion_seller: {
      name: "Gavion Seller",
      desc: 'All of "Sell Gavion" passed.',
    },
    gavion_expert: {
      name: "Gavion Expert",
      desc: 'All of "Gavion in depth" passed.',
    },
  },
  states: {
    locked: "Locked",
    available: "To do",
    in_progress: "In progress",
    done: "Completed",
  },
  map: {
    title: "Mission map",
    kind_game: "Mini-game",
    kind_field: "In the field",
    play: "Play",
    replay: "Play again",
    locked_hint: "Complete the previous mission to unlock it.",
    auto_hint: "Completes itself when your calls show it.",
    best: "Best score: %{score}%",
    attempts: "%{smart_count} attempt |||| %{smart_count} attempts",
    reward: "+%{xp} XP",
    bonus: "+%{xp} XP if perfect",
    progress: "%{count} / %{target}",
  },
  missions: {
    ready: {
      title: "Ready to dial",
      desc: "Verify your softphone in Settings → Phone (test button, then “I heard the call”).",
      cta: "Set up the phone",
    },
    golden_hours: {
      title: "Golden hours",
      desc: "When may you call? %{count} questions on the permitted hours (CRTC).",
    },
    read_queue: {
      title: "Read the queue",
      desc: "Put 4 leads in the order the queue serves them. %{count} rounds.",
    },
    right_outcome: {
      title: "The right outcome",
      desc: "A call situation, 8 possible outcomes: pick the right one. %{count} rounds.",
    },
    hotkeys: {
      title: "Fire fingers",
      desc: "%{count} situations, one key each. Fast and right!",
    },
    crisis: {
      title: "Crisis situations",
      desc: "Pause, offline, released lead… What does the dialer do? %{count} questions.",
    },
    first_session: {
      title: "First session",
      desc: "Start a calling session.",
      cta: "Call",
    },
    ten_calls: {
      title: "10 calls",
      desc: "Log 10 calls with an outcome.",
      cta: "Call",
    },
    first_contact: {
      title: "First contact",
      desc: "Talk to a real person: Talked – call back, Demo booked, Brochure sent or Not interested.",
      cta: "Call",
    },
    first_callback: {
      title: "First scheduled callback",
      desc: "Log a “Talked – call back” with a callback date.",
      cta: "Call",
    },
    first_demo: {
      title: "First demo",
      desc: "Book a demo (Demo booked outcome, or a deal with a demo date).",
      cta: "Call",
    },
    contact_call: {
      title: "Call a contact",
      desc: "Call a contact from their page (Call button).",
      cta: "See contacts",
    },
    zero_unlogged: {
      title: "Nothing forgotten",
      desc: "No call waiting in “To complete” (after at least one logged call).",
      cta: "Open “To complete”",
    },
    ...productMissionsEn,
  },
  game: {
    question: "Question %{n} of %{total}",
    round: "Round %{n} of %{total}",
    correct: "Correct!",
    wrong: "Not quite.",
    answer_was: "The right answer: %{answer}",
    right_option: "right answer",
    your_option: "your answer",
    next: "Next",
    finish: "See my score",
    score_title: "Your score",
    score: "%{correct} / %{total} — %{score}%",
    passed: "Mission complete!",
    failed: "You need %{pass}% to pass. Try again!",
    perfect: "Perfect! %{xp} XP bonus.",
    xp_gain: "+%{xp} XP",
    retry: "Play again",
    back: "Back to the map",
    quit: "Quit the game",
    saving: "Saving…",
    save_failed:
      "Your score could not be saved. Check your connection and try again.",
    celebration: "Mission complete: %{mission}",
  },
  order: {
    instructions:
      "Put these leads in the order the queue serves them: first served at the top. Use Move up and Move down.",
    position: "Position %{n}",
    move_up: "Move %{name} up",
    move_down: "Move %{name} down",
    check: "Check the order",
    right: "Perfect order!",
    wrong: "Not quite. Here is the queue's order:",
    rule_title: "The queue rule",
    rule: {
      1: "My due callbacks (oldest first)",
      2: "Unassigned due callbacks",
      3: "Never called: mine, then unassigned; high quality, then best score",
      4: "Retries: last call more than %{hours} h ago (oldest first)",
    },
  },
  hotkeys: {
    intro:
      "A situation appears: press the dialer shortcut. You have %{seconds} seconds per situation. The keyboard is only captured during the round; Esc to give up.",
    start: "Start the round",
    area: "Shortcut game area",
    press: "Press the right key",
    progress: "Situation %{n} of %{total}",
    hit: "Nice!",
    miss: "Missed: it was %{key}.",
    timeout: "Too late: it was %{key}.",
    accuracy: "Accuracy: %{percent}%",
    speed: "Average time: %{seconds} s",
    score: "%{score} points",
    record: "Record: %{score} points",
    new_record: "New record!",
    quit: "Give up (Esc)",
    keys_title: "Dialer shortcuts",
  },
  content: {
    golden_hours: {
      yes: "Yes, I call",
      no: "No, I wait",
      tue_late: {
        prompt:
          "It is %{time} in Montréal on a %{day}. Your lead is in Montréal. May you call?",
        explanation:
          "No. On weekdays the window is %{weekday_start} to %{weekday_end}, in the lead's local time. At %{lead_time} it is too late: the dialer will not serve the lead.",
      },
      sat_morning: {
        prompt:
          "It is %{time} in Québec City on a %{day}. Your lead is in Québec City. May you call?",
        explanation:
          "Yes. On weekends you call from %{weekend_start} to %{weekend_end}. %{lead_time} is inside the window.",
      },
      vancouver: {
        prompt:
          "It is %{time} in Montréal on a %{day}. Your lead is in Vancouver. May you call?",
        explanation:
          "No. The lead's clock is what counts: it is %{lead_time} in Vancouver, before %{weekday_start}. The dialer checks each lead's local time.",
      },
      sun_close: {
        prompt:
          "It is %{time} sharp in Toronto on a %{day}. Your lead is in Toronto. May you call?",
        explanation:
          "No. The weekend window ends at %{weekend_end}, and the end time is excluded: at %{lead_time} sharp it is already closed. It opens at %{weekend_start}.",
      },
      holidays: {
        prompt:
          "It's December 25 (or January 1), 10:30 a.m. What does the dialer do?",
        blocked:
          "It calls no one all day, by the lead's local date: the next window is the next day at opening.",
        weekend_hours: "It calls with the weekend hours.",
        config_list: "It depends on the holiday list in the call settings.",
        explanation:
          "On December 25 and January 1 there are no calls, every year, by the lead's local date: on the evening of the 24th a Vancouver lead can still be called while it's already Christmas in Halifax. The dialer shows “public holiday” and the next window (the next day at opening). The holiday list in the settings never unblocks these two days: it only counts business days for automations.",
      },
    },
    read_queue: {
      cards: {
        callback_mine: "Callback due %{hours} h ago, assigned to you",
        callback_unassigned: "Callback due %{hours} h ago, unassigned",
        callback_unassigned_min: "Callback due %{minutes} min ago, unassigned",
        never_hq: "Never called, high quality",
        never_hq_score: "Never called, high quality, score %{score}",
        never_hq_unassigned: "Never called, high quality, unassigned",
        never_score: "Never called, score %{score}",
        never_mine_score: "Never called, score %{score}, assigned to you",
        retry: "Retry: last call %{hours} h ago",
      },
      r1: {
        explanation:
          "Your due callbacks come first, then unassigned due callbacks, then never-called leads, and retries last.",
      },
      r2: {
        explanation:
          "A due callback comes first. Among never-called leads, high quality beats score: a high-quality lead at 20 comes before an ordinary lead at 40.",
      },
      r3: {
        explanation:
          "Among never-called leads, the ones assigned to you come before unassigned ones, even high quality. Retries come last, oldest first (at least %{hours} h after the last call).",
      },
    },
    right_outcome: {
      rings_out: {
        prompt: "It rings… and rings… and rings. Nobody answers, no voicemail.",
        explanation:
          "No answer (%{key}): nobody on the line and no message left. The lead stays “in progress” and will come back in the queue.",
      },
      voicemail: {
        prompt:
          "“You have reached Plomberie Gagnon, please leave a message.” You leave your message after the beep.",
        explanation:
          "Voicemail (%{key}): you left a message on the voicemail. The lead stays “in progress”.",
      },
      callback_thursday: {
        prompt: "“I'm on a job site. Call me back Thursday around 2 PM.”",
        explanation:
          "Talked – call back (%{key}), with the callback date and time: required, in the future and inside the lead's calling hours. The queue serves the lead back to you then.",
      },
      demo_tuesday: {
        prompt: "“OK, show me. Does Tuesday 10 AM work for you?”",
        explanation:
          "Demo booked (%{key}): enter the date and time, and a %{durations}-minute duration. The lead leaves the calling queue.",
      },
      send_brochure: {
        prompt: "“Email me your brochure, I'll have a look.”",
        explanation:
          "Brochure sent (%{key}): a valid email is required when the lead does not have one yet.",
      },
      simple_no: {
        prompt: "“No thanks, I'm not interested.” And he politely hangs up.",
        explanation:
          "Not interested (%{key}), with a reason: the lead is paused for %{days} days. Do not call is only for a prospect who explicitly asks not to be called again.",
      },
      wrong_company: {
        prompt:
          "“This is Garage Tremblay… No, no plumbing here, wrong number.”",
        explanation:
          "Wrong number (%{key}): the number is marked invalid and the lead leaves the calling queue.",
      },
      never_again: {
        prompt: "“Take my number off your list. Never call me again.”",
        explanation:
          "Do not call (%{key}): it is permanent. You confirm the prospect's request, and their number (and email, if any) goes on the Do not call list. Not to be confused with Not interested (%{days}-day pause).",
      },
      consent: {
        prompt:
          "The prospect says: “Send me the brochure.” Nothing else about your emails. You pick Brochure sent. The “%{box}” box?",
        tick: "I tick it: he wants the brochure",
        leave: "I leave it empty: he did not say yes to our emails",
        explanation:
          "The “%{box}” box records an express consent (CASL). Tick it only when the prospect clearly agrees to receive your emails.",
      },
    },
    crisis: {
      long_pause: {
        prompt:
          "You pause the session (Space) for a coffee… and come back %{minutes} minutes later. The lead on screen?",
        released: "It was released: it goes back to the queue",
        kept: "It stays yours while the session is open",
        session_ends: "The session ends by itself",
        explanation:
          "After %{minutes} minutes of pause on the same card, the lead is released for the others. The dialer warns you.",
      },
      offline: {
        prompt: "Your Wi-Fi drops just as you save a call outcome.",
        queued: "The outcome is kept and sent when you reconnect",
        lost: "The outcome is lost, start over",
        queue_moves: "The queue keeps moving as if nothing happened",
        explanation:
          "Offline, outcomes are kept on your device (up to %{max}) and sent in order when you reconnect. The queue does not move while you are offline. Only the email consent is not recorded offline: redo it.",
      },
      lease_lost: {
        prompt:
          "You just talked to the prospect. As you save, a “%{title}” window opens.",
        save_anyway: "Save anyway",
        skip: "Skip",
        close_tab: "Close the tab",
        explanation:
          "The lock expired, but you really called: pick “%{save}” to keep your call's outcome. “Skip” is only for when you did not call.",
      },
      crash: {
        prompt: "Your browser crashes mid-call, before you pick an outcome.",
        to_complete: "The call becomes “Unlogged” and waits in “To complete”",
        lost: "The call disappears",
        dnc: "The lead becomes Do not call",
        explanation:
          "A call without an outcome becomes “Unlogged” after %{minutes} minutes and shows in “%{list}”. Open the list and complete the outcome.",
      },
      wrong_outcome: {
        prompt:
          "2 hours ago you picked No answer by mistake: you had actually left a message.",
        edit: "I fix the outcome from the lead's history",
        call_again: "I call the lead again to fix it",
        impossible: "Nothing can be done anymore",
        explanation:
          "You can change the outcome of your own call for %{hours} h: “%{action}” button on the call in the history.",
      },
      locked: {
        prompt: "On a lead's page you see “In progress — Martin”.",
        dont_call: "I don't call: Martin is calling this lead",
        call_anyway: "I call anyway, first come first served",
        dnc_list: "The lead is on the Do not call list",
        explanation:
          "“In progress” means another rep holds this lead right now. The Call button is disabled: you do not call at the same time.",
      },
      recording: {
        prompt: "A prospect asks you: “Are you recording this call?”",
        no_recording:
          "No: the CRM records no calls, my notes are taken in Granola",
        read_notice: "I first read the recording notice of the script",
        yes_quality: "Yes, for quality, but they may refuse",
        explanation:
          "The CRM records no calls: the feature was removed. There is no recording notice to read and no “refuses recording” box. Your call notes are taken in Granola. %{toggle}",
      },
    },
    hotkeys: {
      prompts: {
        rings_out: "It rings out, no voicemail",
        left_message: "You left a message on the voicemail",
        call_me_thursday: "“Call me back Thursday”",
        demo_yes: "“OK for a demo on Tuesday!”",
        send_info: "“Send me the brochure”",
        not_interested: "“No thanks, not interested”",
        wrong_company: "Wrong company on the line",
        never_again: "“Never call me again”",
        skip: "Skip this lead without calling",
        note: "Write a note",
        objections: "Look up an answer to an objection",
        space: "Coffee break (or cancel the countdown)",
        save: "Save the outcome",
        help: "Show the shortcut help",
      },
      keys: {
        space: "Space",
        ctrl_enter: "Ctrl+Enter",
      },
    },
  },
  team: {
    title: "Team progress",
    name: "Rep",
    level: "Level",
    missions: "Missions",
    xp: "XP",
    last_activity: "Last activity",
    never: "Never",
    empty: "No active user.",
    product: "Product",
  },
  nudge: {
    title: "Training: %{done}/%{total} missions",
    body: "Complete the missions to become a Dialer pro.",
    cta: "Continue the training",
    dismiss: "Hide the training from the dashboard",
  },
  dialer: {
    link: "Training",
    hint: "Dialer training: %{done}/%{total} missions.",
    hint_cta: "Continue",
  },
  product: {
    ...trainingProductEn,
    catalog: {
      option: "%{value}",
      price_prompt: "How much is « %{name} » per month?",
      package_prompt: "Which package costs %{price} per month?",
      addon_prompt: "How much is the « %{name} » add-on?",
      explanation: "Prices in Canadian dollars, per month, before GST and QST.",
      addon_unit_explanation:
        "Billed per unit used in the month, before GST and QST.",
    },
  },
};
